import { createContext, useCallback, useContext, useEffect, useState } from "react";
import api from "../lib/api";
import { useAuth } from "./AuthContext";

/**
 * Coach first-run status, fetched once per page load (issue #755) — the
 * coach counterpart of `OnboardingContext.js`, mirrored deliberately
 * (see that file's header for the full reasoning; only the differences
 * are restated here).
 *
 * WHY A CONTEXT AND NOT A HOOK PER CONSUMER. Two consumers mount at the
 * same time under `Layout.jsx`: `CoachSetupGate` (the redirect) and, once
 * a coach is ON `/coach-setup`, the screen itself — which needs `refresh`/
 * `applyStatus` to stop the gate re-firing the instant "Continue" or
 * either "Skip" completes. One provider, one request per page load.
 *
 * COACH-ONLY, STRUCTURALLY. `GET /coach-profiles/me` 403s for a player and
 * (per that route's own role gate) for an admin too — this provider never
 * makes the request for either, the same "no request, no state, no gate"
 * posture `OnboardingProvider` takes for a non-player.
 *
 * IT FAILS OPEN. A failed read leaves `required` false, so the app renders
 * exactly as it did before this feature existed — the same asymmetry
 * `OnboardingContext.js` argues for: costing one coach one skipped prompt
 * is cheap, holding every coach behind a flaked request is not.
 *
 * `profile` RIDES THE SAME FETCH (issue #897). `CoachSetUp.jsx` used to
 * start its own `headline`/`about` state at `""` unconditionally, so
 * reopening the screen — from "Invite a player", or just by navigating
 * back, which `CoachSetupGate.jsx`'s own header says is always legal —
 * showed blank fields, and "Continue"/either "Skip" then PUT those blanks
 * straight over a saved profile (`coachProfiles.ts`'s `normalise`: an
 * empty string is "clear it", not "leave it"). The fetch that decides
 * `required` already carries the coach's own headline/about (`GET
 * /coach-profiles/me`'s `toApiCoachProfile`), so this exposes it instead
 * of making the screen fetch a second copy — same "one provider, one
 * request per page load" posture the rest of this file argues for.
 */
const CoachSetupCtx = createContext(null);

const EMPTY = Object.freeze({
  required: false,
  loading: false,
  profile: null,
  refresh: async () => {},
  applyStatus: () => {},
});

export function CoachSetupProvider({ children }) {
  const { user } = useAuth();
  const isCoach = user?.role === "coach";
  const [required, setRequired] = useState(false);
  const [loading, setLoading] = useState(isCoach);
  const [profile, setProfile] = useState(null);

  const refresh = useCallback(async () => {
    if (!isCoach) {
      setRequired(false);
      setProfile(null);
      setLoading(false);
      return null;
    }
    setLoading(true);
    try {
      const { data } = await api.get("/coach-profiles/me");
      setRequired(!!data?.coach_setup_required);
      setProfile(data ?? null);
      return data;
    } catch (e) {
      // See the header: a failed read is not evidence this coach needs
      // the set-up, and it is not grounds for holding them in it.
      console.error("CoachSetupProvider: failed to read coach setup status", e);
      setRequired(false);
      return null;
    } finally {
      setLoading(false);
    }
  }, [isCoach]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  /** Push a status the server just returned (from the Continue/Skip PUT)
   * straight into context — same shape as OnboardingContext's applyStatus,
   * for the same reason: the gate must stop redirecting in the same tick
   * the coach acts, not after a second round trip. The PUT's response is
   * the same `toApiCoachProfile` shape as the GET, so it is a legitimate
   * `profile` update too — keeps the screen's next render consistent with
   * what it just saved, instead of going stale until the next `refresh()`. */
  const applyStatus = useCallback((data) => {
    setRequired(!!data?.coach_setup_required);
    if (data) setProfile(data);
  }, []);

  if (!isCoach) {
    return <CoachSetupCtx.Provider value={EMPTY}>{children}</CoachSetupCtx.Provider>;
  }

  return (
    <CoachSetupCtx.Provider value={{ required, loading, profile, refresh, applyStatus }}>
      {children}
    </CoachSetupCtx.Provider>
  );
}

/** Safe outside the provider — returns the inert value, so an isolated
 * render (a test, a future route outside the protected layout) never
 * crashes on a missing provider. */
export const useCoachSetup = () => useContext(CoachSetupCtx) || EMPTY;
